import { useTranslation } from '@frontend/i18n';
import { TextField, InlineNotification, Text, Accordion } from '@frontend/design-system';
import { useRuleRecurrenceForm } from '../../../hooks/settings/use-rule-recurrence-form';
import { AvailabilityFormRow } from '../availability-form/atoms/availability-form-row';
import { AvailabilityFormSection } from '../availability-form/atoms/availability-form-section';
import {
  AvailabilityMode,
  BookingChannelEnablement,
  GetAvailabilityRuleFormFieldProps,
} from '../availability-form/types';
import { BookingChannelRulesForm } from './organisms/booking-channel-rules-form';
import { LegacyBookingRulesForm } from './organisms/legacy-booking-rules-form';
import { ProviderForm } from './organisms/provider-form';
import { RuleRecurrenceForm } from './organisms/rule-recurrence-form';
import { ServicesForm } from './organisms/services-form';

type Props = {
  locationId: string | undefined;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  ruleRecurrenceFormProps: ReturnType<typeof useRuleRecurrenceForm<GetAvailabilityRuleFormFieldProps>>;
  availabilityMode: AvailabilityMode;
  isBookingRuleWithoutServices: boolean;
  bookingChannelEnablement: BookingChannelEnablement;
  isBookingChannelVisibilityEnabled: boolean;
};

export const AvailabilityRuleForm = ({
  locationId,
  providerList,
  operatoryList,
  getFieldProps,
  ruleRecurrenceFormProps,
  availabilityMode,
  isBookingRuleWithoutServices,
  bookingChannelEnablement,
  isBookingChannelVisibilityEnabled,
}: Props) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <div>
      <AvailabilityFormSection title={t('Display Name')} hasBottomBorder={false}>
        <AvailabilityFormRow>
          <TextField label={t('')} {...getFieldProps('displayName')} placeholder={t('Name')} />
        </AvailabilityFormRow>
      </AvailabilityFormSection>
      <RuleRecurrenceForm locationId={locationId} ruleRecurrenceFormProps={ruleRecurrenceFormProps} />
      <ProviderForm
        providerList={providerList}
        operatoryList={operatoryList}
        getFieldProps={getFieldProps}
        availabilityMode={availabilityMode}
      />
      <Accordion isMulti startingValue={['booking-rules', 'services']}>
        {isBookingChannelVisibilityEnabled ? (
          <BookingChannelRulesForm
            locationId={locationId}
            getFieldProps={getFieldProps}
            bookingChannelEnablement={bookingChannelEnablement}
          />
        ) : (
          <LegacyBookingRulesForm locationId={locationId} getFieldProps={getFieldProps} />
        )}
        <ServicesForm locationId={locationId} getFieldProps={getFieldProps} />
      </Accordion>
      {isBookingRuleWithoutServices && (
        <>
          {isBookingChannelVisibilityEnabled ? (
            <InlineNotification status='warn' size='small' css={{ div: { width: '100%' } }}>
              <div css={{ display: 'flex', flexDirection: 'column' }}>
                <Text size='medium'>
                  {t(
                    'At least 1 service needs to be selected when the availability is visible to Online Scheduling or AI Receptionist.'
                  )}
                </Text>
              </div>
            </InlineNotification>
          ) : (
            <InlineNotification status='warn' size='small' css={{ div: { width: '100%' } }}>
              <div css={{ display: 'flex', flexDirection: 'column' }}>
                <Text size='medium'>{t('“Self Bookable” requires selecting services.')}</Text>
                <Text size='medium'>{t('Change booking rules or services to continue')}</Text>
              </div>
            </InlineNotification>
          )}
        </>
      )}
    </div>
  );
};
